import { hostParticipant } from "../data/simulatedParticipants";
import type {
  AvatarTone,
  QuizRoundDefinition,
  QuizRoundResult,
  RoundDefinition,
  RoundResult,
  SimulatedParticipant,
} from "../game-engine/types";
import type { Envelope, HostRoomState, HostScreen, Player, QuizQuestion } from "./types";

// The host UI phase. Adds "countdown" (not in the simulation mockup) so the
// host can render a countdown screen between lobby and question.
export type HostPhase =
  | "lobby"
  | "countdown"
  | "question"
  | "answering"
  | "reveal"
  | "summary";

export interface HostUiState {
  code: string;
  phase: HostPhase;
  participants: SimulatedParticipant[]; // non-host players, in join order
  currentRoundIndex: number;
  totalQuestions: number;
  answeredParticipantIds: string[];
  hostAnswerId: string | null;
  results: RoundResult[]; // accumulated per-round partycast results
  runId: number;
  autoJoinStopped: boolean;
  // derived
  currentRound: RoundDefinition | undefined;
  currentResult: RoundResult | undefined;
  roundParticipants: SimulatedParticipant[]; // [host, ...participants]
  // raw / extras
  startTime: number | null;
  countdown: number;
  qrPayload: string | null;
  connected: boolean;
}

export function initHostState(code: string): HostUiState {
  return {
    code,
    phase: "lobby",
    participants: [],
    currentRoundIndex: 0,
    totalQuestions: 10,
    answeredParticipantIds: [],
    hostAnswerId: null,
    results: [],
    runId: 0,
    autoJoinStopped: true, // phones join themselves; "Anslut alla" is a no-op live
    currentRound: undefined,
    currentResult: undefined,
    roundParticipants: [hostParticipant],
    startTime: null,
    countdown: 3,
    qrPayload: null,
    connected: false,
  };
}

// ---- translation helpers ----

export function toSimulatedParticipant(player: Player): SimulatedParticipant {
  return {
    id: player.id,
    name: player.name,
    initials: deriveInitials(player.name),
    tone: colorToTone(player.color),
    color: player.color,
    emoji: player.avatar,
  };
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  const initials = parts.slice(0, 2).map((p) => p[0] ?? "").join("").toUpperCase();
  return initials || "??";
}

function colorToTone(hex: string): AvatarTone {
  const { r, g, b } = hexToRgb(hex);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return "blue";
  const hue = rgbToHue(r, g, b);
  if (hue < 20 || hue >= 340) return "coral";
  if (hue < 50) return "amber";
  if (hue < 90) return "amber";
  if (hue < 160) return "mint";
  if (hue < 210) return "blue";
  if (hue < 280) return "violet";
  return "rose";
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m || !m[1]) return { r: NaN, g: NaN, b: NaN };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHue(r: number, g: number, b: number): number {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const d = max - min;
  if (d === 0) return 0;
  let h = 0;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  h = Math.round(h * 60);
  return h < 0 ? h + 360 : h;
}

function buildQuizRound(q: QuizQuestion, idx: number): QuizRoundDefinition {
  return {
    id: `round-${idx}`,
    type: "quiz",
    prompt: q.q,
    options: q.opts.map((label, i) => ({ id: String(i), label })),
    correctOptionId: String(q.correct),
    maxPoints: 1000,
  };
}

// Build a partycast QuizRoundResult from the post-reveal player roster
// (players' lastAnswer/lastPoints are populated by the server on reveal).
function buildQuizRoundResult(
  roundId: string,
  players: Record<string, Player>,
): QuizRoundResult {
  const answers = Object.values(players).map((p) => ({
    participantId: p.id,
    optionId: p.lastAnswer != null ? String(p.lastAnswer) : "",
    awardedPoints: p.lastPoints,
  }));
  return { roundId, type: "quiz", answers };
}

// ---- phase mapping ----

function mapPhase(server: HostScreen, hostAnswerId: string | null): HostPhase {
  switch (server) {
    case "lobby": return "lobby";
    case "countdown": return "countdown";
    case "question": return hostAnswerId != null ? "answering" : "question";
    case "reveal": return "reveal";
    case "final": return "summary";
  }
}

const HOST_ANSWERED_SENTINEL = "__answered__";

// ---- envelope application ----

export function applyEnvelope(state: HostUiState, env: Envelope): HostUiState {
  const p = (env.payload ?? {}) as Record<string, unknown>;
  switch (env.type) {
    case "room-created":
      return {
        ...state,
        code: (p.code as string) ?? state.code,
        qrPayload: (p.qrPayload as string) ?? null,
      };

    case "host-state":
      return applyHostState(state, p as unknown as HostRoomState);

    case "host-question":
      return applyHostQuestion(state, p);

    case "host-reveal":
      return applyHostReveal(state, p);

    case "host-final":
      return { ...state, phase: "summary" };

    case "player-joined":
      return addParticipant(state, p.player as Player);

    case "player-left":
      return removeParticipant(state, p.playerId as string);

    case "player-answered":
      return addAnswered(state, p.playerId as string);

    case "ws-state":
      return { ...state, connected: (p.connected as boolean) ?? state.connected };

    default:
      return state;
  }
}

function applyHostState(state: HostUiState, s: HostRoomState): HostUiState {
  const players = Object.values(s.players);
  const nonHost = players.filter((pl) => pl.id !== hostParticipant.id);
  const participants = nonHost.map(toSimulatedParticipant);
  const roundParticipants = [hostParticipant, ...participants];

  // Determine the host's answer state from the roster (reconnect-safe).
  let hostAnswerId = state.hostAnswerId;
  const hostInAnswered = s.answeredPlayerIds.includes(hostParticipant.id);
  if (hostInAnswered && hostAnswerId == null) hostAnswerId = HOST_ANSWERED_SENTINEL;
  if (!hostInAnswered && hostAnswerId === HOST_ANSWERED_SENTINEL) hostAnswerId = null;

  const phase = mapPhase(s.phase, hostAnswerId);
  const currentRound = s.currentQuestion
    ? buildQuizRound(s.currentQuestion, s.questionIdx)
    : state.currentRound;

  return {
    ...state,
    code: s.code,
    phase,
    participants,
    roundParticipants,
    currentRoundIndex: s.questionIdx,
    totalQuestions: s.totalQuestions,
    answeredParticipantIds: s.answeredPlayerIds,
    hostAnswerId,
    startTime: s.startTime,
    countdown: s.countdown,
    currentRound,
  };
}

function applyHostQuestion(state: HostUiState, p: Record<string, unknown>): HostUiState {
  const q = p.question as QuizQuestion;
  const idx = (p.questionIdx as number) ?? state.currentRoundIndex;
  const startTime = (p.startTime as number) ?? null;
  const totalQuestions = (p.totalQuestions as number) ?? state.totalQuestions;
  return {
    ...state,
    phase: "question",
    currentRoundIndex: idx,
    totalQuestions,
    currentRound: buildQuizRound(q, idx),
    currentResult: undefined,
    startTime,
    hostAnswerId: null,
    answeredParticipantIds: [],
  };
}

function applyHostReveal(state: HostUiState, p: Record<string, unknown>): HostUiState {
  const players = p.players as Record<string, Player> | undefined;
  if (!players) return { ...state, phase: "reveal" };
  const roundId = `round-${state.currentRoundIndex}`;
  const result = buildQuizRoundResult(roundId, players);
  const results = [...state.results.filter((r) => r.roundId !== roundId), result];
  return {
    ...state,
    phase: "reveal",
    results,
    currentResult: result,
  };
}

function addParticipant(state: HostUiState, player: Player | undefined): HostUiState {
  if (!player) return state;
  if (player.id === hostParticipant.id) return state;
  if (state.participants.some((p) => p.id === player.id)) return state;
  const participant = toSimulatedParticipant(player);
  const participants = [...state.participants, participant];
  return { ...state, participants, roundParticipants: [hostParticipant, ...participants] };
}

function removeParticipant(state: HostUiState, playerId: string): HostUiState {
  const participants = state.participants.filter((p) => p.id !== playerId);
  const answeredParticipantIds = state.answeredParticipantIds.filter((id) => id !== playerId);
  return {
    ...state,
    participants,
    roundParticipants: [hostParticipant, ...participants],
    answeredParticipantIds,
  };
}

function addAnswered(state: HostUiState, playerId: string): HostUiState {
  if (!playerId || state.answeredParticipantIds.includes(playerId)) return state;
  return {
    ...state,
    answeredParticipantIds: [...state.answeredParticipantIds, playerId],
  };
}

// Mark the host's answer locally (optimistic) — called by the hook when the
// host picks an option. Returns state with hostAnswerId set to the option id.
export function markHostAnswer(state: HostUiState, optionId: string): HostUiState {
  if (state.hostAnswerId != null) return state;
  return {
    ...state,
    hostAnswerId: optionId,
    answeredParticipantIds: state.answeredParticipantIds.includes(hostParticipant.id)
      ? state.answeredParticipantIds
      : [...state.answeredParticipantIds, hostParticipant.id],
    phase: state.phase === "question" ? "answering" : state.phase,
  };
}

// Reset for "play again".
export function resetHostState(state: HostUiState): HostUiState {
  return {
    ...initHostState(state.code),
    qrPayload: state.qrPayload,
    runId: state.runId + 1,
  };
}